// React
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'

// Components
import Period from 'store/v2/react/scenes/cart/period'
import Lines from 'store/v2/react/scenes/cart/lines'
import Services from 'store/components/Services'
import Overdue from 'store/v2/react/scenes/cart/components/Overdue'
import Tooltip from 'ui/components/Tooltip'
import { PoweredBy } from 'shop-components/components/PoweredBy'

// Libraries
import { StyleSheetManager } from 'styled-components'

// Shared
import ClientActions from 'store/v2/redux/actions/client'
import Format from 'shared/utils/format'
import ReactCSSTransitionGroup from 'shared/lib/react_css_transition_group'
import { withTranslation } from 'shared/utils/withTranslation'
import previewUrlComposer from 'shared/utils/previewUrlComposer'
import { shouldForwardProp } from 'shared/utils/shouldForwardProp'

class FloatingCart extends Component {
  static displayName = 'FloatingCart'

  static propTypes = {
    cartOpen: PropTypes.bool,
    toggleCart: PropTypes.func,
    cart: PropTypes.object,
    locations: PropTypes.object,
    hasDates: PropTypes.bool,
    cannotCheckout: PropTypes.bool,
    showPrices: PropTypes.bool,
    showPoweredBy: PropTypes.bool,
    totalPriceAttribute: PropTypes.string,
    currency: PropTypes.object,
    checkingOut: PropTypes.bool,
    setCheckingOut: PropTypes.func,
    viewCartHref: PropTypes.string,
    checkoutButtonValid: PropTypes.bool,
    cartOverdue: PropTypes.bool,
    inPortal: PropTypes.bool,
    nonServiceCartLinesCount: PropTypes.number,
    t: PropTypes.func
  }

  state = {
    autoOpenedModal: false
  }

  onAutoModal = (callback) => {
    // Needs to live in parent state as Period gets unmounted
    // on some transitions losing state
    this.setState({ autoOpenedModal: true }, callback)
  }

  renderCheckoutButton = () => {
    const { checkoutButtonValid, hasDates, cannotCheckout, setCheckingOut, viewCartHref, t } = this.props
    const checkoutButtonClass = checkoutButtonValid ? 'bq-enabled' : 'bq-disabled'

    const checkoutButton = (
      <button onClick={setCheckingOut} className={`bq-branded bq-button ${checkoutButtonClass}`}>
        {t('user.store.checkout')}
      </button>
    )

    let tooltipContent = t('user.store.select_a_period')

    if (hasDates && cannotCheckout) {
      tooltipContent = t('user.store.not_all_products_available')
    }

    return (
      <div className="button-group">
        {viewCartHref && (
          <a href={window.Booqable.decorateLinkForTracking(viewCartHref)} className="bq-branded bq-button">
            {t('user.store.view_cart')}
          </a>
        )}
        {checkoutButtonValid && (
          checkoutButton
        )}
        {!checkoutButtonValid && (
          <Tooltip direction="bottom" content={tooltipContent}>
            {checkoutButton}
          </Tooltip>
        )}
      </div>
    )
  }

  render () {
    const { autoOpenedModal } = this.state
    const {
      cart,
      cartOpen,
      toggleCart,
      cartOverdue,
      cannotCheckout,
      checkingOut,
      hasDates,
      showPrices,
      showPoweredBy,
      currency,
      totalPriceAttribute,
      inPortal,
      locations,
      nonServiceCartLinesCount,
      t
    } = this.props
    const floatingCartClasses = []

    floatingCartClasses.push(cartOpen ? 'open' : 'closed')

    if (inPortal) {
      floatingCartClasses.push('portal')
    }

    return (
      <ReactCSSTransitionGroup
        transitionName="booqable-sidebar"
        transitionEnterTimeout={100}
        transitionLeaveTimeout={100}
      >
        <div
          id="booqable-sidebar-inner"
          key={`booqable-sidebar-${cartOpen}`}
          data-tid="Mini cart"
          className={floatingCartClasses.join(' ')}
        >
          <div className="bq-sidebar-head">
            <button className="bq-branded bq-close" onClick={toggleCart.bind(null, false)}>
              <i className="bq-icon-cross fas fa-times" />
            </button>
            <div className="bq-branded bq-title">{t('user.store.my_order')}</div>
            <StyleSheetManager target={document.body} shouldForwardProp={shouldForwardProp} enableVendorPrefixes>
              <Period
                cart={cart}
                hasDates={hasDates}
                locations={locations}
                autoOpenedModal={autoOpenedModal}
                onAutoModal={this.onAutoModal}
              />
            </StyleSheetManager>
          </div>
          <Lines toggleCart={toggleCart} cart={cart} cannotCheckout={cannotCheckout} hasDates={hasDates} />
          <ReactCSSTransitionGroup
            transitionName="booqable-sidebar-summary"
            transitionEnterTimeout={150}
            transitionLeaveTimeout={150}
            component="div"
          >
            {nonServiceCartLinesCount > 0 && (
              <div id="booqable-sidebar-summary">
                {cart && (
                  <StyleSheetManager target={document.body} shouldForwardProp={shouldForwardProp} enableVendorPrefixes>
                    <Services cart={cart} />
                  </StyleSheetManager>
                )}
                {showPrices && (
                  <div>
                    <div className="bq-detail bq-strong">
                      <span>{t('user.store.subtotal')}</span>
                      <span className="bq-amount">{Format.money(cart?.get(totalPriceAttribute), currency)}</span>
                    </div>

                    {cart !== null && cart.get('deposit_in_cents') !== 0 && (
                      <div className="bq-detail">
                        <span>{t('user.store.security_deposit')}</span>
                        <span className="bq-amount">{Format.money(cart.get('deposit_in_cents'), currency)}</span>
                      </div>
                    )}

                    <Overdue show={cartOverdue} />
                  </div>
                )}
                {checkingOut && (
                  <div className="bq-loading-wrapper">
                    <div className="bq-loader" />
                  </div>
                )}
                {!checkingOut && this.renderCheckoutButton()}
                {showPoweredBy && (
                  <div className="bq-poweredby">
                    <PoweredBy logoWidth="16px" />
                  </div>
                )}
              </div>
            )}
          </ReactCSSTransitionGroup>
        </div>
      </ReactCSSTransitionGroup>
    )
  }
}

const mapStateToProps = (state, props) => {
  let cannotCheckout, totalPriceAttribute

  const slug = state.client.getIn(['company', 'slug'])
  const cart = state.orm.carts.getById(state.client.cartId)

  const cartLines = state.orm.lines.getById(cart.lines)
  const nonServiceCartLinesCount = cartLines.pluck('product_type').filter((type) => type !== 'service').length

  const cartOverdue = state.client.cartOverdue

  const searchParams = new URLSearchParams(window.location.search)
  const href = window.booqableOptions.preview ?
    previewUrlComposer({ slug, themeId: searchParams.get('theme_id'), key: 'templates/cart.json' }) :
    props.href || cart?.cart_url

  if (state.settings.get('pricing.tax_strategy') === 'inclusive') {
    totalPriceAttribute = 'grand_total_with_tax_in_cents'
  } else {
    totalPriceAttribute = 'grand_total_in_cents'
  }

  const hasDates = !!(cart?.starts_at && cart?.stops_at)
  const useAvailability = state.settings.get('store.use_availability')

  // Check if we need to calculate if use can checkout
  if (useAvailability) {
    cannotCheckout =
      state.orm.lines
        .getAll({
          id: cart?.lines,
          can_checkout: false
        })
        .size() > 0
  } else {
    cannotCheckout = false
  }

  // Make sure to typecast to boolean
  const checkoutButtonValid = !!(nonServiceCartLinesCount && hasDates && !cannotCheckout && !cartOverdue)

  return {
    cart,
    nonServiceCartLinesCount,
    cartOpen: state.client.cartOpen,
    cartScroll: state.client.cartScroll,
    currency: state.client.currency?.toJS(),
    totalPriceAttribute,
    checkoutButtonValid,
    hasDates,
    checkingOut: state.client.checkingOut,
    cannotCheckout,
    showPrices: state.settings.get('store.use_prices'),
    showPoweredBy: state.settings.get('store.show_powered_by'),
    cartOverdue,
    locations: state.orm.locations.getAll().sort('name'),
    viewCartHref: href
  }
}

const mapDispatchToProps = () => {
  return {
    toggleCart: (cartOpen) => {
      return ClientActions.toggleCart(cartOpen)
    },
    setCheckingOut: (e) => {
      e.preventDefault()

      if (ClientActions.evaluateCartOverdue()) return

      ClientActions.setCheckingOut()
    }
  }
}

export default compose(reduxConnect(mapStateToProps, mapDispatchToProps), withTranslation('user'))(FloatingCart)
